import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BACKEND_URL } from "@/lib/backendUrl";
import { B2bPicker } from "@/components/calendar/B2bPicker";

const API = `${BACKEND_URL}/api`;
const F = ({ label, children }) => <label className="block text-xs text-slate-400 space-y-1"><span>{label}</span>{children}</label>;
const inp = "h-9 bg-slate-950/60 border-slate-700 text-sm";

const digits = (v) => (v || "").replace(/\D/g, "");

// Form state, phone lookup and actions. Owned by QuickAddDialog only.
function useQuickAdd(date, { onClose, onCreated }) {
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [found, setFound] = useState(null);
  const [b2b, setB2b] = useState([]);
  useEffect(() => { if (date) axios.get(`${API}/customers`).then(({ data }) => setB2b(data.filter((c) => c.is_business).sort((a, b) => (a.company || a.name).localeCompare(b.company || b.name)))).catch(() => {}); }, [date]);
  useEffect(() => { if (date) { setF({ plate: "", customer_name: "", customer_phone: "", vehicle: "", total: "", scheduled_date: date, expected_done: "", job_notes: "" }); setFound(null); } }, [date]);
  useEffect(() => {
    if (!f || f.customer_id) return;
    if (digits(f.customer_phone).length < 8) { setFound(null); return; }
    const t = setTimeout(() => axios.get(`${API}/vehicles/lookup`, { params: { phone: f.customer_phone } }).then(({ data }) => setFound(data.found ? data : null)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [f?.customer_phone, f?.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const attach = () => setF({ ...f, customer_name: found.customer.name, customer_phone: found.customer.phone, customer_id: found.customer.id });
  const detach = () => { const { customer_id, ...rest } = f; setF({ ...rest, customer_name: "", customer_phone: "" }); setFound(null); };
  const pickVehicle = (v) => setF({ ...f, plate: v.plate, vehicle: v.make_model || f.vehicle });
  const pickB2b = (c) => {
    if (!c) return detach();
    setFound({ customer: { id: c.id, name: c.name, phone: c.phone }, vehicles: c.vehicles || [], open_jobs: 0 });
    setF({ ...f, customer_id: c.id, customer_name: c.name, customer_phone: c.phone || "" });
  };
  const submit = async () => {
    if (!f.plate.trim()) return toast.error("Registration number is required");
    try { const { data } = await axios.post(`${API}/jobs`, { ...f, total: Number(f.total || 0), expected_done: f.expected_done || null }); toast.success(`${data.plate} added to ${f.scheduled_date}`); onCreated(); onClose(); }
    catch (e) { toast.error("Could not add job", { description: e?.response?.data?.detail }); }
  };
  const openFullQuote = () => {
    const p = new URLSearchParams();
    if (f.customer_id) {
      p.set("customer", f.customer_id);
      const v = found?.vehicles?.find((x) => x.plate === f.plate.trim().toUpperCase());
      if (v) p.set("vehicle", v.id);
    } else {
      if (f.customer_name) p.set("name", f.customer_name);
      if (f.customer_phone) p.set("phone", f.customer_phone);
    }
    if (f.plate.trim()) p.set("plate", f.plate.trim().toUpperCase());
    if (f.vehicle) p.set("make", f.vehicle);
    p.set("scheduled", f.scheduled_date);
    onClose();
    navigate(`/quote?${p.toString()}`);
  };
  return { f, setF, set, found, b2b, attach, detach, pickVehicle, pickB2b, submit, openFullQuote };
}

// "Existing customer found → Link" banner, or the linked-customer panel with vehicle chips
function CustomerLinkPanel({ f, found, attach, detach, pickVehicle }) {
  if (found && !f.customer_id) {
    return (
      <div className="col-span-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs flex items-center justify-between gap-2" data-testid="qa-existing">
        <span>Existing customer found: <b>{found.customer.name}</b> · {found.customer.phone} · {found.vehicles.length} vehicle(s){found.open_jobs ? ` · ${found.open_jobs} ongoing job(s)` : ""}</span>
        <Button size="sm" onClick={attach} data-testid="qa-attach" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-7">Link customer</Button>
      </div>
    );
  }
  if (!f.customer_id) return null;
  return (
    <div className="col-span-2 space-y-1.5" data-testid="qa-attached">
      <div className="text-xs text-emerald-300 flex items-center justify-between gap-2"><span>Linked to existing customer <b>{f.customer_name}</b> — no duplicate record will be created.</span><button type="button" onClick={detach} data-testid="qa-detach" className="text-slate-400 hover:text-slate-200 underline">Unlink</button></div>
      {found?.vehicles?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[11px] text-slate-500 self-center">Vehicles on file:</span>
          {found.vehicles.map((v) => <button key={v.id} type="button" onClick={() => pickVehicle(v)} data-testid={`qa-vehicle-${v.plate}`} data-on={f.plate === v.plate} className="px-2 h-6 rounded-full border border-slate-700 text-[11px] font-mono-num text-slate-300 hover:border-amber-400 data-[on=true]:bg-amber-500 data-[on=true]:text-slate-950 data-[on=true]:border-amber-500">{v.plate}{v.make_model ? ` · ${v.make_model}` : ""}</button>)}
        </div>
      )}
    </div>
  );
}

function JobFields({ f, set, setF }) {
  return (
    <>
      <F label="Registration *"><Input value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} data-testid="qa-plate" className={`${inp} uppercase font-mono-num`} placeholder="SGA1234A" /></F>
      <F label="Vehicle make / model"><Input value={f.vehicle} onChange={set("vehicle")} data-testid="qa-vehicle" className={inp} placeholder="BMW X5" /></F>
      <F label="Final amount · 金额 ($)"><Input type="number" min="0" step="0.01" value={f.total} onChange={set("total")} data-testid="qa-total" className={`${inp} font-mono-num`} placeholder="0" /></F>
      <F label="Scheduled date"><Input type="date" value={f.scheduled_date} onChange={set("scheduled_date")} data-testid="qa-scheduled" className={`${inp} [color-scheme:dark]`} /></F>
      <F label="Est. completion"><Input type="date" value={f.expected_done} min={f.scheduled_date} onChange={set("expected_done")} data-testid="qa-expected" className={`${inp} [color-scheme:dark]`} /></F>
      <F label="Notes"><Input value={f.job_notes} onChange={set("job_notes")} data-testid="qa-notes" className={inp} /></F>
    </>
  );
}

// Quick-add a job on a date. Existing phone number → attach to that customer instead of duplicating.
export default function QuickAddDialog({ date, onClose, onCreated }) {
  const { f, setF, set, found, b2b, attach, detach, pickVehicle, pickB2b, submit, openFullQuote } = useQuickAdd(date, { onClose, onCreated });
  if (!f) return null;
  return (
    <Dialog open={!!date} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#111827] border-slate-700 text-slate-100" data-testid="quick-add-dialog">
        <DialogHeader><DialogTitle>Add vehicle · 新增车辆</DialogTitle><DialogDescription className="text-slate-400">Schedule a vehicle on {date}. Pick a B2B client from the list, or enter the customer's phone number to link to their record on file.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {b2b.length > 0 && <div className="col-span-2"><B2bPicker clients={b2b} value={f.customer_id && b2b.some((c) => c.id === f.customer_id) ? f.customer_id : ""} onPick={pickB2b} /></div>}
          <F label="Customer phone"><Input value={f.customer_phone} onChange={set("customer_phone")} data-testid="qa-phone" className={`${inp} font-mono-num`} placeholder="9123 4567" inputMode="tel" disabled={!!f.customer_id} autoFocus /></F>
          <F label="Customer name"><Input value={f.customer_name} onChange={set("customer_name")} data-testid="qa-customer" className={inp} disabled={!!f.customer_id} /></F>
          <CustomerLinkPanel f={f} found={found} attach={attach} detach={detach} pickVehicle={pickVehicle} />
          <JobFields f={f} set={set} setF={setF} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button onClick={submit} data-testid="qa-submit" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">Add job · 新增</Button>
          <Button onClick={openFullQuote} variant="outline" data-testid="qa-open-quote" className="border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800 hover:text-amber-300"><FileText className="w-4 h-4 mr-1.5" /> Open full quote · 报价</Button>
        </div>
        <p className="text-[11px] text-slate-500 -mt-1">Full quote opens the pricing calculator with these details pre-filled; it lands on this date once approved.</p>
      </DialogContent>
    </Dialog>
  );
}
